import { Injectable } from '@angular/core';
import {HttpClient, HttpEvent, HttpHeaders} from "@angular/common/http";
import {DataService} from "../data-service/data.service";
import {Router} from "@angular/router";
import {ToastService} from "../toast-service/toast.service";
import {Observable, of} from "rxjs";
import {catchError, shareReplay, tap} from "rxjs/operators";
import {environment} from "../../../../environments/environment";
import {ApiRequests} from "../../api.routes";

@Injectable({
  providedIn: 'root'
})
export class TenantApiService {

  baseUrl = environment.baseUrlTest;
  private cache = new Map<string, Observable<any>>();

  constructor(private http: HttpClient, private dataService: DataService, private router: Router,
              private toastService: ToastService) {
  }
  // Replaces environment.supportBaseUrl with the tenant-specific URL stored at login.
  // Falls back transparently when no override is set.
  private resolveSupportUrl(url: string): string {
    if (!url.startsWith(environment.supportBaseUrl)) return url;
    const resolved = this.dataService.getSupportBaseUrl();
    if (resolved === environment.supportBaseUrl) return url;
    return url.replace(environment.supportBaseUrl, resolved.replace(/\/$/, ''));
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // Request was cancelled by the user (navigation/unmount) — suppress toast
      if (error.status === 0 && error.error instanceof ProgressEvent && error.error.type === 'abort') {
        return of(result as T);
      }

      if(typeof error.error =="object" && error.error !=undefined && Object.keys(error.error).includes('message')) {
        this.toastService.showError(`${error.error.message}`,'Request Failed')
      }else{
        this.toastService.showError(`${error.message}`,`${operation} request failed`)
      }
      return of(error.error);
    };

    /**
     *
     * $.get()
     */
  }

  get<T>(endpoint: string, headers: any = null, useCache: boolean = false): Observable<T | HttpEvent<any>> {
    endpoint = this.resolveSupportUrl(endpoint);
    const tenantId = this.dataService.getTenant()?.id ?? 'anon';
    const cacheKey = `${tenantId}:${endpoint}${headers ? JSON.stringify(headers) : ''}`;

    if (useCache && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    let options: any = {};
    if (headers) {
      options.headers = new HttpHeaders(headers);
    }

    const fullUrl = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}/${endpoint}`;
    const request$ = this.http.get<T | HttpEvent<any>>(fullUrl, options).pipe(
      shareReplay(1),
      catchError(err => {
        this.cache.delete(cacheKey);
        return this.handleError<any>('get')(err);
      })
    );

    if (useCache) {
      this.cache.set(cacheKey, request$);
    }

    return request$;
  }

  clearCache() {
    this.cache.clear();
  }

  getPdf<T>(endpoint: string,headers:any=null) {

    return this.http.get(`${this.baseUrl}/${endpoint}`, {
      responseType: 'blob'  // 👈 This is the key
    });
  }

  post<T>(endpoint: string, data: any, headers: any = null): Observable<T> {
    endpoint = this.resolveSupportUrl(endpoint);
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}/${endpoint}`;
    const request$ = headers
      ? this.http.post<T>(fullUrl, data, { headers: new HttpHeaders(headers) })
      : this.http.post<T>(fullUrl, data);
    return request$.pipe(catchError(this.handleError<T>('post')));
  }

  put<T>(endpoint: string, data: any, headers: any = null): Observable<T> {
    endpoint = this.resolveSupportUrl(endpoint);
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}/${endpoint}`;
    const request$ = headers
      ? this.http.put<T>(fullUrl, data, { headers: new HttpHeaders(headers) })
      : this.http.put<T>(fullUrl, data);
    return request$.pipe(catchError(this.handleError<T>('put')));
  }

  patch<T>(endpoint: string, data: any): Observable<T> {
    endpoint = this.resolveSupportUrl(endpoint);
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}/${endpoint}`;
    return this.http.patch<T>(fullUrl, data).pipe(
      catchError(this.handleError<T>('patch'))
    );
  }

  delete<T>(endpoint: string, headers: any = null): Observable<T> {
    endpoint = this.resolveSupportUrl(endpoint);
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}/${endpoint}`;
    const options = headers ? { headers: new HttpHeaders(headers) } : {};
    return this.http.delete<T>(fullUrl, options).pipe(
      catchError(this.handleError<T>('delete'))
    );
  }

  /** GET with no error toast — errors are swallowed and null is emitted instead. */
  silentGet<T>(endpoint: string, headers: any = null): Observable<T | null> {
    endpoint = this.resolveSupportUrl(endpoint);
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}/${endpoint}`;
    const options = headers ? { headers: new HttpHeaders(headers as Record<string, string>) } : {};
    return (this.http.get<T>(fullUrl, options) as Observable<T>).pipe(
      catchError(() => of(null))
    );
  }

  /** DELETE with no error toast — errors are swallowed and null is emitted instead. */
  silentDelete<T>(endpoint: string): Observable<T | null> {
    endpoint = this.resolveSupportUrl(endpoint);
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}/${endpoint}`;
    return this.http.delete<T>(fullUrl).pipe(
      catchError(() => of(null))
    );
  }

  /** POST with no error toast — errors are swallowed and null is emitted instead. */
  silentPost<T>(endpoint: string, data: any): Observable<T | null> {
    endpoint = this.resolveSupportUrl(endpoint);
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}/${endpoint}`;
    return this.http.post<T>(fullUrl, data).pipe(
      catchError(() => of(null))
    );
  }

  /** Returns the current user from SupportService. Result is cached for the session. */
  getCurrentUser(): Observable<any> {
    return this.get<any>(`${environment.supportBaseUrl}/${ApiRequests.SupportGetUser}`, null, true);
  }

  /** Returns all claims for the logged-in user from SupportService. */
  getUserClaims(): Observable<any[]> {
    return this.get<any[]>(`${environment.supportBaseUrl}/${ApiRequests.SupportGetUserClaims}`) as Observable<any[]>;
  }

  /** Clears the cached current user so the next call fetches fresh data. */
  clearCurrentUserCache(): void {
    const tenantId = this.dataService.getTenant()?.id ?? 'anon';
    const url = this.resolveSupportUrl(`${environment.supportBaseUrl}/${ApiRequests.SupportGetUser}`);
    this.cache.delete(`${tenantId}:${url}`);
  }
}
